import { omit } from 'lodash-es'
import type { Nullable } from '@/types'
import type { PaginationUrlFilterScalarValueType, PaginationUrlFilterSingleType } from '@/services/tables/types'
import { isRecordUnknown } from '@/utils/typeGuards'

export const contactNotesFilterNamePrefix = 'notes'
const contactNotesFilterNameSeparator = ':'

export const CONTACT_NOTES_FILTER_FIELDS = ['createdAt', 'text'] as const

export type ContactNotesFilterField = (typeof CONTACT_NOTES_FILTER_FIELDS)[number]

export type ContactNotesFilterName<Field extends ContactNotesFilterField = ContactNotesFilterField> =
  `${typeof contactNotesFilterNamePrefix}:${Field}`

export const isContactNotesFilterField = (field: string): field is ContactNotesFilterField => {
  return (CONTACT_NOTES_FILTER_FIELDS as readonly string[]).includes(field)
}

export const buildContactNotesFilterName = <Field extends ContactNotesFilterField>(field: Field) => {
  return [contactNotesFilterNamePrefix, field].join(contactNotesFilterNameSeparator) as ContactNotesFilterName<Field>
}

export const parseContactNotesFilterName = (filterName: string): Nullable<ContactNotesFilterField> => {
  const [prefix, field, ...rest] = filterName.split(contactNotesFilterNameSeparator)

  if (prefix !== contactNotesFilterNamePrefix || rest.length > 0) {
    return null
  }

  return isContactNotesFilterField(field) ? field : null
}

export const isContactNotesFilterName = (filterName: string): filterName is ContactNotesFilterName => {
  return parseContactNotesFilterName(filterName) !== null
}

export const expandContactNotesFilters = <T extends PaginationUrlFilterSingleType>(filter: T): T => {
  const contactNotesFilters: unknown = filter[contactNotesFilterNamePrefix]

  if (!isRecordUnknown(contactNotesFilters)) {
    return filter
  }

  const result: PaginationUrlFilterSingleType = omit(filter, contactNotesFilterNamePrefix)

  Object.entries(contactNotesFilters).forEach(([field, value]) => {
    if (isContactNotesFilterField(field)) {
      result[buildContactNotesFilterName(field)] = value as PaginationUrlFilterScalarValueType
    }
  })

  return result as T
}
